import { useEffect, useState } from "react";
import { Loader2, Monitor, RotateCcw, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { FeaturedCarousel, loadFeaturedCfg } from "../../components/FeaturedCarousel";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { Card, Pair, Color, Range } from "./VerifyPromoSettings";

const SAMPLE = [
  { id: "s1", title: "French Bulldog puppies, available today!", category: "dogs", description: "Eight weeks old, vet checked and raised with kids.", price: 980, price_type: "fixed", city: "Laval", province: "QC", photos: ["https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=900"] },
  { id: "s2", title: "Ragdoll kittens — blue point", category: "cats", description: "Sweet, cuddly and litter trained.", price: 850, price_type: "fixed", city: "Ottawa", province: "ON", photos: ["https://images.unsplash.com/photo-1574158622682-e40e69881006?w=900"] },
  { id: "s3", title: "Baby budgies, hand fed", category: "birds", description: "Bright colours, very social.", price: 60, price_type: "fixed", city: "Calgary", province: "AB", photos: ["https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=900"] },
  { id: "s4", title: "Holland Lop bunnies", category: "small-pets", description: "Friendly and used to handling.", price: 120, price_type: "fixed", city: "Halifax", province: "NS", photos: ["https://images.unsplash.com/photo-1585110396000-c9ffd4e4b308?w=900"] },
  { id: "s5", title: "Leopard gecko with setup", category: "reptiles", description: "Tank, heat mat and hides included.", price: 200, price_type: "fixed", city: "Victoria", province: "BC", photos: ["https://images.unsplash.com/photo-1504450874802-0ba2bcd9b5ae?w=900"] },
];

const Choice = ({ s, set, k, label, options }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 mb-2">{label}</p>
    <div className="flex flex-wrap gap-1">{options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => set(k, v)} data-testid={`feat-${k}-${v}`}
        className={`h-8 px-3 rounded-lg text-xs font-bold ${s[k] === v ? "bg-[#12234A] text-white" : "bg-white text-slate-500 border"}`}>{l}</button>
    ))}</div>
  </div>
);

const Toggle = ({ s, set, k, label }) => (
  <label className="flex items-center gap-2 text-sm text-[#12234A]"><Switch checked={s[k]} onCheckedChange={(v) => set(k, v)} data-testid={`feat-${k}`} /> {label}</label>
);

export function FeaturedSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("desktop");
  const [real, setReal] = useState(null);
  useEffect(() => {
    api.get("/admin/site/featured-box").then(({ data }) => setS(data)).catch((e) => toast.error(errText(e)));
    api.get("/listings/featured").then(({ data }) => setReal(data)).catch(() => setReal([]));
  }, []);
  if (!s) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/site/featured-box", s); setS((p) => ({ ...p, ...data })); loadFeaturedCfg(true); toast.success("Featured spotlight saved"); }
    catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };
  const tab = (on) => `h-8 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 ${on ? "bg-[#12234A] text-white" : "bg-white text-slate-500 border"}`;
  const items = real && real.length >= 2 ? real : SAMPLE;

  return (
    <div className="space-y-5" data-testid="featured-settings">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-3"><Switch checked={s.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="feat-enabled" />
          <span className="text-sm font-semibold text-[#12234A]">Show Featured spotlight (homepage & category pages)</span></label>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => { setS({ ...s.defaults, defaults: s.defaults }); toast("Original design loaded — press Save to keep it"); }} className="rounded-xl gap-2" data-testid="feat-reset"><RotateCcw size={15} /> Original design</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-2" data-testid="feat-save">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 sticky top-2 z-10" data-testid="feat-preview">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Live preview {items === SAMPLE ? "· sample ads" : ""} {!s.enabled && "· hidden"}</p>
          <div className="flex gap-1">
            <button type="button" onClick={() => setMode("desktop")} className={tab(mode === "desktop")} data-testid="feat-preview-desktop"><Monitor size={13} /> Computer</button>
            <button type="button" onClick={() => setMode("mobile")} className={tab(mode === "mobile")} data-testid="feat-preview-mobile"><Smartphone size={13} /> Phone</button>
          </div>
        </div>
        <div className={`${mode === "mobile" ? "max-w-[380px] mx-auto" : ""} -mx-4 sm:-mx-6 lg:-mx-8 -mt-6 max-h-[42vh] overflow-auto`}>
          <FeaturedCarousel cfg={{ ...s, enabled: true }} previewItems={items} mode={mode} />
        </div>
      </div>

      <Card title="Layout">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Ads shown at once</p>
              <div className="flex gap-1">{[1, 2, 3, 4].map((n) => (
                <button key={n} type="button" onClick={() => set("per_view", n)} data-testid={`feat-per-view-${n}`}
                  className={`h-9 w-10 rounded-lg text-sm font-bold ${s.per_view === n ? "bg-[#F5551E] text-white" : "bg-slate-100 text-[#12234A]"}`}>{n}</button>
              ))}</div>
              <p className="text-[11px] text-slate-400 mt-1">1 = one large spotlight. 2–4 = smaller cards side by side (phones always show one).</p>
            </div>
            <Range s={s} set={set} k="height_desktop" label={s.per_view > 1 ? "Card height on computers" : "Height on computers"} min={220} max={640} pre="feat" />
            <Range s={s} set={set} k="height_mobile" label="Photo height on phones" min={100} max={400} pre="feat" />
            {s.per_view === 1 && <Choice s={s} set={set} k="photo_side" label="Photo side" options={[["left", "Left"], ["right", "Right"]]} />}
            <Choice s={s} set={set} k="photo_split" label="How much of each card is photo" options={[[50, "Half"], [60, "60%"], [70, "70%"]]} />
          </div>
          <div className="space-y-5">
            <Range s={s} set={set} k="speed" label="Seconds per slide" min={2} max={20} unit="s" step={0.5} pre="feat" />
            <Toggle s={s} set={set} k="pause_hover" label="Pause while the mouse is over it" />
            <Choice s={s} set={set} k="transition" label="Transition" options={[["fade", "Fade"], ["slide", "Slide"], ["zoom", "Zoom"]]} />
            <Range s={s} set={set} k="max_slides" label="Maximum ads in rotation" min={1} max={12} unit="" step={1} pre="feat" />
          </div>
        </div>
      </Card>

      <Card title="Show or hide">
        <div className="grid sm:grid-cols-3 gap-3">
          {[["show_category", "Category"], ["show_description", "Description"], ["show_price", "Price"], ["show_location", "Location"], ["show_arrows", "Arrows"], ["show_dots", "Dots"]].map(([k, l]) => <Toggle key={k} s={s} set={set} k={k} label={l} />)}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="feat-repeats-box">
          <Toggle s={s} set={set} k="allow_repeats" label="Spotlight ads may also appear in Near you and Fresh listings" />
          <p className="text-xs text-slate-400 mt-1 ml-11">Turn off so homepage visitors see each spotlight ad only once, in the spotlight.</p>
        </div>
      </Card>

      <Card title="Colours">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Color s={s} set={set} k="panel_color" label="Panel" pre="feat" />
          <Color s={s} set={set} k="text_color" label="Text" pre="feat" />
          <Color s={s} set={set} k="accent_color" label="Accent & badge" pre="feat" />
          <Color s={s} set={set} k="button_bg" label="Button" pre="feat" />
          <Color s={s} set={set} k="button_text" label="Button text" pre="feat" />
        </div>
      </Card>

      <Card title="Wording">
        <div className="space-y-4">
          <Pair s={s} set={set} k="title" label="Heading (homepage)" max={60} pre="feat" />
          <Pair s={s} set={set} k="subtitle" label="Small subtitle (empty English hides it)" max={90} pre="feat" />
          <Pair s={s} set={set} k="badge" label="Photo badge (empty English hides it)" max={24} pre="feat" />
          <Pair s={s} set={set} k="button" label="Button text (empty English hides it)" max={30} pre="feat" />
        </div>
      </Card>
    </div>
  );
}
